import { useState } from 'react';
import { useSelector } from 'hooks';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import type { GridRowId } from '@mui/x-data-grid-pro';
import LoadingOverlay from 'components/shared/LoadingOverlay';
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import WarningDialog from 'components/shared/WarningDialog/WarningDialog';
import { toNativeDate } from 'utils/date';
import Header from './components/Header';
import RejectReasonDialog from './components/RejectReasonDialog';
import {
  HoursPayOutAction,
  MAX_REQUESTS_PER_ACTION
} from './HoursPayOutRequestsOverview.types';
import type { HoursPayOutRequestRow } from './HoursPayOutRequestsOverview.types';
import { useHoursPayOutProcessing } from './hooks/useHoursPayOutProcessing';
import useColumnDefinitions from './useColumnDefinitions';
import { usePlanUnitSelection } from './hooks/usePlanUnitSelection';
import { usePeriodAndDateState } from './hooks/usePeriodAndDateState';
import { useDateRangeCalculation } from './hooks/useDateRangeCalculation';
import { useHoursPayOutData } from './hooks/useHoursPayOutData';

const HoursPayOutRequestsOverview = () => {
  'use memo';

  const { t } = useTranslation('common');
  const { hasHoursPayOutRequestApprove } = useSelector((state) => state.grants);

  const columns = useColumnDefinitions();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // The ids a dialog actually acts on, captured once when it opens - kept separate from
  // selectedIds (the checkbox selection) because the grid can update that selection on its own
  // (e.g. its internal row-click handling) after a row action has already fired, which raced with
  // reusing selectedIds directly and could submit an empty id list.
  const [actionIds, setActionIds] = useState<string[]>([]);
  const [openAction, setOpenAction] = useState<HoursPayOutAction | null>(null);

  const { setSelectedPlanUnits, organizationalUnitIds } =
    usePlanUnitSelection();

  const {
    period,
    setPeriod,
    selectedDate,
    setSelectedDate,
    selectedDateRange,
    setSelectedDateRange
  } = usePeriodAndDateState();

  const dateRange = useDateRangeCalculation({
    period,
    selectedDate,
    selectedDateRange
  });

  const { rows, isLoading, isError, refetch, onlyToApprove, setOnlyToApprove } =
    useHoursPayOutData({
      dateRange,
      organizationalUnitIds
    });

  const { payOut, reject, isProcessing } = useHoursPayOutProcessing();

  const closeDialog = () => {
    setOpenAction(null);
    setActionIds([]);
    setSelectedIds([]);
  };

  /** A row action acts on that one request, through the same dialogs and calls as the bulk action. */
  const handleRowAction = (id: string, action: HoursPayOutAction) => {
    setActionIds([id]);
    setOpenAction(action);
  };

  const handleBulkAction = (action: HoursPayOutAction) => {
    // the header already blocks this, so it is only a backstop against sending a batch the
    // endpoint's maxItems would reject
    if (
      selectedIds.length === 0 ||
      selectedIds.length > MAX_REQUESTS_PER_ACTION
    ) {
      return;
    }
    setActionIds(selectedIds);
    setOpenAction(action);
  };

  const handlePayOut = () => {
    if (actionIds.length === 0) {
      closeDialog();
      return;
    }
    payOut(actionIds);
    closeDialog();
  };

  const handleReject = (statusNote: string) => {
    if (actionIds.length === 0) {
      closeDialog();
      return;
    }
    reject(actionIds, statusNote);
    closeDialog();
  };

  const isSelectable = rows.some(({ disableSelection }) => !disableSelection);

  return (
    <>
      <Header
        selectedDate={selectedDate}
        selectedDateRange={selectedDateRange}
        period={period}
        onlyToApprove={onlyToApprove}
        onPeriodChange={setPeriod}
        onSelectedDate={setSelectedDate}
        onSelectedDateRange={(dateRange) =>
          setSelectedDateRange([
            toNativeDate(dateRange?.[0]),
            toNativeDate(dateRange?.[1])
          ])
        }
        onSelectPlanUnit={setSelectedPlanUnits}
        onOnlyToApproveChange={setOnlyToApprove}
        showActions={hasHoursPayOutRequestApprove}
        numberOfSelectedRequests={selectedIds.length}
        onAction={handleBulkAction}
      />
      <Box>
        <TableCRUDLayout<HoursPayOutRequestRow>
          rows={rows}
          columns={columns}
          hasLoadingError={isError}
          onLoadingError={refetch}
          translationKey={
            onlyToApprove
              ? 'hoursPayOutRequestsOverview.emptyStateToBeApproved'
              : 'hoursPayOutRequestsOverview.emptyState'
          }
          editable={false}
          pageSize={MAX_REQUESTS_PER_ACTION}
          pageSizeOptions={[MAX_REQUESTS_PER_ACTION]}
          hideFooter={rows.length === 0}
          checkboxSelection={hasHoursPayOutRequestApprove && isSelectable}
          customSelectionModel={{
            type: 'include',
            ids: new Set<GridRowId>(selectedIds)
          }}
          onSelectionChange={(_, selectedRows = []) =>
            setSelectedIds(
              selectedRows.map(({ id }: HoursPayOutRequestRow) => id)
            )
          }
          customActions={(_id, row) => [
            {
              tooltipTitle: t('hoursPayOutRequestsOverview.actions.PAY_OUT'),
              icon: 'check',
              onClick: (id: string | number) =>
                handleRowAction(String(id), HoursPayOutAction.PAY_OUT),
              hidden: !hasHoursPayOutRequestApprove || row?.disableSelection
            },
            {
              tooltipTitle: t('hoursPayOutRequestsOverview.actions.REJECT'),
              icon: 'xmark',
              onClick: (id: string | number) =>
                handleRowAction(String(id), HoursPayOutAction.REJECT),
              hidden: !hasHoursPayOutRequestApprove || row?.disableSelection
            }
          ]}
          dynamicHeight
          initialState={{
            sorting: {
              sortModel: [{ field: 'requestDate', sort: 'asc' }]
            }
          }}
        />
      </Box>
      <WarningDialog
        open={openAction === HoursPayOutAction.PAY_OUT}
        title={t(
          actionIds.length > 1
            ? 'hoursPayOutRequestsOverview.payOutTitleMultiple'
            : 'hoursPayOutRequestsOverview.payOutTitleSingle'
        )}
        onSubmit={handlePayOut}
        onCancel={() => setOpenAction(null)}
      />
      <RejectReasonDialog
        open={openAction === HoursPayOutAction.REJECT}
        numberOfRequests={actionIds.length}
        onSubmit={handleReject}
        onCancel={() => setOpenAction(null)}
      />
      <LoadingOverlay loading={isLoading || isProcessing} />
    </>
  );
};

export default HoursPayOutRequestsOverview;
